import React, { useEffect, useRef, useState } from "react";

/**
 * TariffRefundOpportunity.jsx
 * A high-converting landing page component specifically for the U.S. audience.
 * Focus: Tariff Refund / Claim Window Opportunity (effective April 20, 2026).
 */

const TariffRefundOpportunity = () => {
    const sectionRefs = useRef([]);
    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        phone: "",
        companyName: "",
        description: "",
    });

    const [loading, setLoading] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    // Intersection Observer for scroll animations
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("visible");
                    }
                });
            },
            { threshold: 0.1 }
        );

        const currentRefs = sectionRefs.current;
        currentRefs.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => {
            currentRefs.forEach((ref) => {
                if (ref) observer.unobserve(ref);
            });
        };
    }, []);

    const addToRefs = (el) => {
        if (el && !sectionRefs.current.includes(el)) {
            sectionRefs.current.push(el);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.fullName || !formData.email) {
            setSubmitStatus({ type: 'error', message: 'Please fill in all required fields.' });
            return;
        }

        setLoading(true);
        setSubmitStatus(null);

        try {
            const response = await fetch("https://api.pipara.com/api/email/submit", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    formType: "tariff",
                    country: "us",
                    ...formData
                })
            });
            const result = await response.json();
            if (result.success) {
                setSubmitStatus({ type: 'success', message: 'Request submitted successfully!' });
                setFormData({ fullName: "", email: "", phone: "", companyName: "", description: "" });

            } else {
                setSubmitStatus({ type: 'error', message: result.message || 'Submission failed.' });
            }
        } catch (error) {
            console.error("Submission error:", error);
            setSubmitStatus({ type: 'error', message: 'Network error. Please check your backend connection.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="tariff-landing-page">
            <style>
                {`
                :root {
                    --primary-blue: #004289;
                    --light-blue: #0062cc;
                    --text-dark: #2d3436;
                    --text-light: #636e72;
                    --bg-gray: #f8f9fb;
                    --white: #ffffff;
                    --accent-red: #d63031;
                    --transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
                }

                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap');


                .tariff-landing-page {
                    font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
                    color: var(--text-dark);
                    line-height: 1.6;
                    overflow-x: hidden;
                }

                /* Section Animations */
                .animate-up {
                    opacity: 0;
                    transform: translateY(30px);
                    transition: var(--transition);
                }

                .animate-up.visible {
                    opacity: 1;
                    transform: translateY(0);
                }

                /* Hero Section */
                .hero {
                    background: linear-gradient(135deg, #001f3f 0%, #004289 100%);
                    color: var(--white);
                    padding: 100px 20px;
                    text-align: center;
                    position: relative;
                }

                .hero-content {
                    max-width: 900px;
                    margin: 0 auto;
                }

                .hero-badge {
                    background: rgba(255, 255, 255, 0.1);
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    padding: 8px 16px;
                    border-radius: 2px;
                    font-size: 13px;
                    font-weight: 600;
                    display: inline-block;
                    margin-bottom: 24px;
                    text-transform: uppercase;
                    letter-spacing: 1.5px;
                }

                .hero h1 {
                    font-size: 4rem;
                    font-weight: 900;
                    margin-bottom: 24px;
                    line-height: 1.1;
                    letter-spacing: -0.04em;
                    text-transform: uppercase;
                }

                .hero p {
                    font-size: 1.2rem;
                    opacity: 0.85;
                    margin-bottom: 40px;
                    max-width: 650px;
                    margin-left: auto;
                    margin-right: auto;
                    font-weight: 400;
                }

                .cta-button {
                    background: var(--white);
                    color: var(--primary-blue);
                    padding: 18px 40px;
                    font-size: 16px;
                    font-weight: 800;
                    border: none;
                    border-radius: 2px;
                    cursor: pointer;
                    transition: var(--transition);
                    box-shadow: 0 4px 15px rgba(0,0,0,0.2);
                    text-transform: uppercase;
                }

                .cta-button:hover:not(:disabled) {
                    transform: translateY(-3px);
                    background: #f0f0f0;
                    box-shadow: 0 6px 20px rgba(0,0,0,0.25);
                }

                .cta-button:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                /* Container */
                .container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 80px 20px;
                }

                /* Problem Section */
                .section-header {
                    text-align: center;
                    margin-bottom: 60px;
                }

                .section-header h2 {
                    font-size: 2.5rem;
                    color: var(--primary-blue);
                    margin-bottom: 15px;
                }

                .section-header .divider {
                    width: 60px;
                    height: 4px;
                    background: var(--light-blue);
                    margin: 0 auto;
                }

                .problem-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    gap: 30px;
                }

                .feature-card {
                    padding: 30px;
                    background: var(--bg-gray);
                    border-radius: 4px;
                    transition: var(--transition);
                    border-bottom: 3px solid transparent;
                }

                .feature-card:hover {
                    background: var(--white);
                    box-shadow: 0 10px 30px rgba(0,0,0,0.05);
                    border-bottom: 3px solid var(--light-blue);
                }

                .feature-card h3 {
                    margin-bottom: 15px;
                    color: var(--primary-blue);
                    font-size: 1.25rem;
                }

                /* Challenges List */
                .challenges-section {
                    background: var(--white);
                }

                .challenge-list {
                    list-style: none;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                    margin-top: 40px;
                }

                .challenge-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 15px;
                    padding: 20px;
                    background: #fff;
                    border: 1px solid #eee;
                    border-radius: 2px;
                }

                .challenge-item svg {
                    flex-shrink: 0;
                    width: 24px;
                    height: 24px;
                    fill: var(--accent-red);
                    margin-top: 3px;
                }

                /* Process Section */
                .process-section {
                    background: var(--bg-gray);
                }

                .process-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 0;
                    margin-top: 50px;
                }

                .process-step {
                    position: relative;
                    padding: 40px;
                    text-align: center;
                    background: var(--white);
                    border-right: 1px solid #eee;
                }

                .process-step:last-child {
                    border-right: none;
                }

                .step-number {
                    width: 40px;
                    height: 40px;
                    background: var(--primary-blue);
                    color: white;
                    border-radius: 2px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    margin: 0 auto 20px;
                }

                /* Why Pipara Section */
                .why-pipara {
                    background: var(--primary-blue);
                    color: var(--white);
                    padding: 80px 20px;
                    text-align: center;
                }

                .why-pipara h2 { color: white; }

                .trust-features {
                    display: flex;
                    justify-content: center;
                    flex-wrap: wrap;
                    gap: 40px;
                    margin-top: 40px;
                }

                .trust-item {
                    max-width: 250px;
                }

                .trust-item h4 {
                    font-size: 1.1rem;
                    margin-bottom: 10px;
                }

                .trust-item p {
                    font-size: 0.9rem;
                    opacity: 0.8;
                }

                /* Urgency Section */
                .urgency-banner {
                    background: #fff0f0;
                    border: 1px solid #ffcccc;
                    padding: 40px;
                    border-radius: 4px;
                    text-align: center;
                    margin: 60px 0;
                }

                .urgency-banner h3 {
                    color: var(--accent-red);
                    font-size: 1.8rem;
                    margin-bottom: 15px;
                }

                /* CTA Section */
                .final-cta {
                    text-align: center;
                    background: var(--bg-gray);
                    padding: 100px 20px;
                }

                .form-wrapper {
                    background: var(--white);
                    max-width: 600px;
                    margin: 40px auto 0;
                    padding: 50px;
                    border-radius: 4px;
                    box-shadow: 0 30px 60px rgba(0,0,0,0.12);
                    text-align: left;
                }

                .form-field { margin-bottom: 20px; }
                .form-field label { display: block; margin-bottom: 8px; font-weight: 600; }
                .form-field input, .form-field textarea { 
                    width: 100%; 
                    padding: 12px; 
                    border: 1px solid #ddd; 
                    border-radius: 2px;
                    font-family: inherit;
                    font-size: 1rem;
                }
                .form-field input:focus, .form-field textarea:focus {
                    outline: none;
                    border-color: var(--primary-blue);
                    box-shadow: 0 0 0 2px rgba(0, 66, 137, 0.1);
                }

                .status-message {
                    padding: 15px;
                    border-radius: 6px;
                    margin-bottom: 20px;
                    font-weight: 500;
                    text-align: center;
                }
                .status-success { background: #e7f7ef; color: #0a7c42; border: 1px solid #b7e4cd; }
                .status-error { background: #fee; color: #d93025; border: 1px solid #f5c6cb; }

                /* Responsive */
                @media (max-width: 900px) {
                    .hero h1 { font-size: 2.5rem; }
                    .challenge-list { grid-template-columns: 1fr; }
                    .process-step { border-right: none; border-bottom: 1px solid #eee; }
                    .process-step:last-child { border-bottom: none; }
                }

                @media (max-width: 600px) {
                    .hero h1 { font-size: 2rem; }
                    .section-header h2 { font-size: 1.8rem; }
                    .hero { padding: 60px 20px; }
                    .container { padding: 40px 20px; }
                    .form-wrapper { padding: 30px 20px; }
                }
                `}
            </style>

            {/* HERO SECTION */}
            <header className="hero">
                <div className="hero-content animate-up" ref={addToRefs}>
                    <div className="hero-badge">Now Open: Claim Window Effective April 20, 2026</div>
                    <h1>US Tariff Refund NOW</h1>
                    <p>
                        The U.S. authorities have opened a limited-time claim window for tariff refunds.
                        While most businesses are unprepared to substantiate these claims, Pipara turns complexity into liquidity.
                    </p>
                    <button className="cta-button" onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}>
                        Get Free Assessment
                    </button>
                </div>
            </header>

            {/* PROBLEM / WHY THIS MATTERS */}
            <section className="container">
                <div className="section-header animate-up" ref={addToRefs}>
                    <h2>Why This Matters for Your Business</h2>
                    <div className="divider"></div>
                    <p style={{ marginTop: '20px', fontSize: '1.1rem', color: 'var(--text-light)' }}>
                        Significant recoverable amounts remain unclaimed due to historical inconsistencies.
                    </p>
                </div>

                <div className="problem-grid animate-up" ref={addToRefs}>
                    <div className="feature-card">
                        <h3>Classification Errors</h3>
                        <p>Overpaid duties due to incorrect HTS classifications or valuation methods used by previous brokers.</p>
                    </div>
                    <div className="feature-card">
                        <h3>Missed Exemptions</h3>
                        <p>Failure to leverage treaty benefits, special provisions, or Section 301 exclusions correctly.</p>
                    </div>
                    <div className="feature-card">
                        <h3>Documentation Gaps</h3>
                        <p>Lacking the technical documentation required to support historical claims during federal scrutiny.</p>
                    </div>
                    <div className="feature-card">
                        <h3>Compliance Fear</h3>
                        <p>Avoiding filing due to fear of triggering audits. We provide a risk-aware approach to filing.</p>
                    </div>
                </div>
            </section>

            {/* CHALLENGES SECTION */}
            <section className="challenges-section" style={{ background: '#fcfcfc' }}>
                <div className="container">
                    <div className="section-header animate-up" ref={addToRefs}>
                        <h2>Common Challenges Faced by SMEs</h2>
                        <div className="divider"></div>
                    </div>

                    <ul className="challenge-list animate-up" ref={addToRefs}>
                        {[
                            { title: "Data Fragmentation", desc: "Data spread across multiple systems, brokers, and fiscal years." },
                            { title: "Technical Complexity", desc: "Nuanced valuation rules and shifting tariff code regulations." },
                            { title: "Documentation Gaps", desc: "Missing or inconsistent evidence to support legitimate claims." },
                            { title: "Time Constraints", desc: "Internal teams lack the bandwidth to reconstruct historical data." },
                            { title: "Regulatory Risk", desc: "Concern over triggering inquiries or broader customs audits." },
                            { title: "Eligibility Uncertainty", desc: "Not knowing if a claim is worth the pursuit / initial cost." }
                        ].map((item, idx) => (
                            <li key={idx} className="challenge-item">
                                <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" /></svg>
                                <div>
                                    <strong>{item.title}:</strong> {item.desc}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* SOLUTION / PROCESS */}
            <section className="process-section">
                <div className="container">
                    <div className="section-header animate-up" ref={addToRefs}>
                        <h2>Our Solution: End-to-End Claim Support</h2>
                        <div className="divider"></div>
                        <p style={{ marginTop: '20px' }}>A fully managed, forensic-level approach to securing your refunds.</p>
                    </div>

                    <div className="process-grid animate-up" ref={addToRefs}>
                        <div className="process-step">
                            <div className="step-number">1</div>
                            <h3>Eligibility Assessment</h3>
                            <p>Rapid review of your trade profile to identify immediate potential claim areas.</p>
                        </div>
                        <div className="process-step">
                            <div className="step-number">2</div>
                            <h3>Data Reconstruction</h3>
                            <p>Consolidation of import records and classification review to identify overpayments.</p>
                        </div>
                        <div className="process-step">
                            <div className="step-number">3</div>
                            <h3>Computation</h3>
                            <p>Precise, defensible calculation of recoverable amounts aligned with regulations.</p>
                        </div>
                        <div className="process-step">
                            <div className="step-number">4</div>
                            <h3>Filing & Support</h3>
                            <p>Submission and ongoing coordination with customs authorities for claim resolution.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHY PIPARA */}
            <section className="why-pipara animate-up" ref={addToRefs}>
                <div className="container">
                    <h2>Why Pipara?</h2>
                    <div className="divider" style={{ background: 'white', margin: '20px auto' }}></div>
                    <p style={{ maxWidth: '800px', margin: '0 auto 40px' }}>
                        We aren't just advisors; we are execution-focused partners with a forensic DNA.
                    </p>

                    <div className="trust-features">
                        <div className="trust-item">
                            <h4>Global Footprint</h4>
                            <p>Cross-border expertise across USA, India, UAE, and UK.</p>
                        </div>
                        <div className="trust-item">
                            <h4>Forensic DNA</h4>
                            <p>Deep background in handling sensitive, scrutiny-prone financial matters.</p>
                        </div>
                        <div className="trust-item">
                            <h4>Partner-Led</h4>
                            <p>Direct involvement of partners ensures accuracy and accountability.</p>
                        </div>
                        <div className="trust-item">
                            <h4>Execution Focused</h4>
                            <p>We handle the heavy lifting—from data reconstruction to final filing.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* URGENCY BANNER */}
            <section className="container">
                <div className="urgency-banner animate-up" ref={addToRefs}>
                    <h3>Time-Bound Opportunity</h3>
                    <p style={{ fontSize: '1.2rem' }}>
                        Claim windows are strict and non-recurring.
                        <strong> Delays can lead to permanent loss of recoverable amounts.</strong>
                    </p>
                    <p style={{ marginTop: '10px', color: 'var(--text-light)' }}>
                        If your business has U.S. import exposure, an initial assessment is highly recommended.
                    </p>
                </div>
            </section>

            {/* FINAL CTA & FORM */}
            <section className="final-cta" id="contact-form">
                <div className="container animate-up" ref={addToRefs}>
                    <h2>Ready to Recover Your Overpaid Duties?</h2>
                    <p>Reach out for a confidential, no-obligation evaluation of your eligibility.</p>

                    <form className="form-wrapper" onSubmit={handleSubmit}>
                        {submitStatus && (
                            <div className={`status-message status-${submitStatus.type}`}>
                                {submitStatus.message}
                            </div>
                        )}

                        <div className="form-field">
                            <label htmlFor="fullName">Full Name</label>
                            <input
                                type="text"
                                id="fullName"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleChange}
                                placeholder="John Doe"
                                required
                            />
                        </div>
                        <div className="form-field">
                            <label htmlFor="email">Business Email</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="john@company.com"
                                required
                            />
                        </div>
                        <div className="form-field">
                            <label htmlFor="phone">Phone Number</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="+1 (555) 000-0000"
                                required
                            />
                        </div>
                        <div className="form-field">
                            <label htmlFor="companyName">Company Name</label>
                            <input
                                type="text"
                                id="companyName"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleChange}
                                placeholder="Global Trade Inc."
                            />
                        </div>
                        <div className="form-field">
                            <label htmlFor="description">Briefly describe your import/export activity</label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows="4"
                                placeholder="e.g. We import electronic components from SE Asia..."
                            ></textarea>
                        </div>
                        <button
                            type="submit"
                            className="cta-button"
                            style={{ background: 'var(--primary-blue)', color: 'white', width: '100%' }}
                            disabled={loading}
                        >
                            {loading ? 'Submitting...' : 'Submit Assessment Request'}
                        </button>
                    </form>
                </div>
            </section>

            <footer style={{ textAlign: 'center', padding: '40px', color: 'var(--text-light)', borderTop: '1px solid #eee' }}>
                &copy; {new Date().getFullYear()} Pipara & Co LLP. All Rights Reserved. Specific to U.S. Operations.
            </footer>
        </div>
    );
};

export default TariffRefundOpportunity;
